import React from 'react'
import {
  PiCheckCircle,
  PiInfo,
  PiWarning,
  PiWarningCircle,
  PiX,
} from 'react-icons/pi'
import { cn } from 'components/UI/cn'

export type AlertColor = 'success' | 'warning' | 'info' | 'danger' | 'default'

/** A single action button rendered in the alert (Figma "with buttons" variant).
 *  `solid` → filled in the alert's color (primary); `outline` → white/neutral
 *  outline button, the Figma secondary "Dismiss" style; `brand` → DS2026 green
 *  primary (main CTA on neutral/default alerts); `text` → low-emphasis text
 *  button. Defaults to `solid`. The canonical two-button pairing is
 *  `solid` + `outline`. */
export interface AlertAction {
  label: string
  onClick: () => void
  variant?: 'solid' | 'outline' | 'brand' | 'text'
}

interface AlertProps {
  color?: AlertColor
  /** Optional bold heading. When set, renders the titled variant
   *  (p-3, gap-2.5, base/semibold heading + sm/regular body — heading bumped
   *  one step above the body for hierarchy) — tightened from the Figma website
   *  scale to webapp density. Without it, renders the simpler single-line
   *  variant (px-3 py-2, gap-2.5 — same horizontal padding as titled so
   *  stacked alerts align — sm/regular body). */
  title?: string
  description: React.ReactNode
  className?: string
  /** Override the auto-picked icon if needed (defaults: success→CheckCircle,
   *  warning→Warning, info→Info, danger→WarningCircle, default→Info).
   *  Pass `null` to render no icon at all — per the Figma, the icon is
   *  optional and many alerts are icon-less. */
  icon?: React.ComponentType<{ size?: number; className?: string }> | null
  /** When provided, renders a close (X) button in the top-right that calls
   *  this handler. Optional/contextual per the Figma — omit for non-dismissible
   *  alerts. */
  onDismiss?: () => void
  /** Optional action buttons (Figma "with buttons" variant), rendered under
   *  the description. */
  actions?: AlertAction[]
  /** Centered empty-state layout: icon stacked on top, title + description +
   *  actions centered. For full-page / panel error & empty states (e.g. the
   *  importer "An error occurred" screen) rather than inline banners. */
  centered?: boolean
}

const ICON_FOR_COLOR: Record<
  AlertColor,
  React.ComponentType<{ size?: number; className?: string }>
> = {
  success: PiCheckCircle,
  warning: PiWarning,
  info: PiInfo,
  danger: PiWarningCircle,
  default: PiInfo,
}

// Border color uses Tailwind's `!` important modifier because Bootstrap's
// global `.border { ... !important }` utility (shared class name) otherwise
// overrides the token color and renders every alert border gray (#dee2e6).
const CONTAINER_BY_COLOR: Record<AlertColor, string> = {
  success: 'bg-alert-success-bg !border-alert-success-border',
  warning: 'bg-alert-warning-bg !border-alert-warning-border',
  info: 'bg-alert-info-bg !border-alert-info-border',
  danger: 'bg-alert-danger-bg !border-alert-danger-border',
  default: 'bg-alert-default-bg !border-alert-default-border',
}

const TITLE_TEXT_BY_COLOR: Record<AlertColor, string> = {
  success: 'text-alert-success-title',
  warning: 'text-alert-warning-title',
  info: 'text-alert-info-title',
  danger: 'text-alert-danger-title',
  default: 'text-alert-default-title',
}

const BODY_TEXT_BY_COLOR: Record<AlertColor, string> = {
  success: 'text-alert-success-text',
  warning: 'text-alert-warning-text',
  info: 'text-alert-info-text',
  danger: 'text-alert-danger-text',
  default: 'text-alert-default-text',
}

// Close-icon and button colors come from the fuller `ds2026-alert-*` token set
// (the short `alert-*` set only defines bg/border/title/text).
const CLOSE_ICON_BY_COLOR: Record<AlertColor, string> = {
  success: 'text-ds2026-alert-success-close-icon',
  warning: 'text-ds2026-alert-warning-close-icon',
  info: 'text-ds2026-alert-info-close-icon',
  danger: 'text-ds2026-alert-danger-close-icon',
  default: 'text-ds2026-alert-default-close-icon',
}

const BTN_SOLID_BY_COLOR: Record<AlertColor, string> = {
  success:
    'bg-ds2026-alert-success-btn-bg hover:bg-ds2026-alert-success-btn-hover-bg',
  warning:
    'bg-ds2026-alert-warning-btn-bg hover:bg-ds2026-alert-warning-btn-hover-bg',
  info: 'bg-ds2026-alert-info-btn-bg hover:bg-ds2026-alert-info-btn-hover-bg',
  danger:
    'bg-ds2026-alert-danger-btn-bg hover:bg-ds2026-alert-danger-btn-hover-bg',
  default:
    'bg-ds2026-alert-default-btn-bg hover:bg-ds2026-alert-default-btn-hover-bg',
}

const BTN_TEXT_BY_COLOR: Record<AlertColor, string> = {
  success: 'text-ds2026-alert-success-btn-color',
  warning: 'text-ds2026-alert-warning-btn-color',
  info: 'text-ds2026-alert-info-btn-color',
  danger: 'text-ds2026-alert-danger-btn-color',
  default: 'text-ds2026-alert-default-btn-color',
}

/**
 * DS2026 alert. Mirrors the Figma "Alerts" component (node 7172:1687):
 * - Without `title`: single-line variant — px-3 py-2, gap-2.5, sm/regular body
 *   colored with the title token. Icon top-aligns so wrapped text reads right.
 * - With `title`: titled variant — p-3, gap-2.5, 18px icon, base/semibold
 *   heading on top, sm/regular description below (compacted from the Figma
 *   website scale for webapp density; heading kept one step above the body).
 * - With `centered`: empty-state layout — icon stacked on top, everything
 *   center-aligned. For full-page / panel error & empty states.
 * - `icon` is optional (pass `null` for the icon-less variants).
 * - `onDismiss` adds the optional close (X) button; `actions` adds the
 *   optional button row.
 */
export function Alert({
  color = 'default',
  title,
  description,
  className,
  icon,
  onDismiss,
  actions,
  centered = false,
}: AlertProps) {
  const Icon = icon === undefined ? ICON_FOR_COLOR[color] : icon
  const hasTitle = title != null
  const hasActions = actions != null && actions.length > 0

  // Danger alerts use role="alert" (assertive — interrupts screen readers
  // immediately for errors). Other colors use role="status" (polite — read
  // when the user is idle).
  const ariaRole = color === 'danger' ? 'alert' : 'status'

  // Shared action row + dismiss button, reused across the inline and centered
  // layouts so their styling stays in lockstep.
  const actionRow = hasActions ? (
    <div
      className={cn(
        'mt-1 flex flex-wrap items-center gap-2',
        centered && 'justify-center'
      )}
    >
      {actions.map((action, index) => (
        <button
          key={index}
          type="button"
          onClick={action.onClick}
          className={cn(
            'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            (action.variant ?? 'solid') === 'brand' &&
              'bg-ds2026-btn-primary-bg text-white hover:bg-ds2026-btn-primary-hover-bg',
            (action.variant ?? 'solid') === 'solid' &&
              cn('text-white', BTN_SOLID_BY_COLOR[color]),
            action.variant === 'outline' &&
              'border border-ds2026-btn-outline-stroke bg-ds2026-btn-outline-bg text-ds2026-btn-outline-text hover:bg-ds2026-btn-outline-hover-bg',
            action.variant === 'text' &&
              cn('hover:opacity-70', BTN_TEXT_BY_COLOR[color])
          )}
        >
          {action.label}
        </button>
      ))}
    </div>
  ) : null

  const dismissButton = onDismiss ? (
    <button
      type="button"
      onClick={onDismiss}
      aria-label="Dismiss"
      className={cn(
        'flex h-5 shrink-0 items-center justify-center rounded p-0.5 transition-opacity hover:opacity-70',
        CLOSE_ICON_BY_COLOR[color],
        centered && 'absolute right-2 top-2'
      )}
    >
      <PiX size={16} />
    </button>
  ) : null

  if (centered) {
    return (
      <div
        role={ariaRole}
        className={cn(
          'relative flex flex-col items-center gap-2 rounded-lg border p-6 text-center tracking-[-0.2px]',
          CONTAINER_BY_COLOR[color],
          className
        )}
      >
        {Icon && (
          <Icon
            size={32}
            className={cn('shrink-0', TITLE_TEXT_BY_COLOR[color])}
          />
        )}
        <div className="flex max-w-prose flex-col gap-1">
          {hasTitle && (
            <p
              className={cn(
                'text-base font-semibold leading-6',
                TITLE_TEXT_BY_COLOR[color]
              )}
            >
              {title}
            </p>
          )}
          <div
            className={cn(
              'text-sm font-normal leading-5',
              hasTitle ? BODY_TEXT_BY_COLOR[color] : TITLE_TEXT_BY_COLOR[color]
            )}
          >
            {description}
          </div>
        </div>
        {actionRow}
        {dismissButton}
      </div>
    )
  }

  return (
    <div
      role={ariaRole}
      className={cn(
        'flex rounded-lg border tracking-[-0.2px]',
        CONTAINER_BY_COLOR[color],
        hasTitle ? 'items-start gap-2.5 p-3' : 'items-center gap-2.5 px-3 py-2',
        className
      )}
    >
      {Icon && (
        <Icon
          size={hasTitle ? 18 : 20}
          className={cn(
            'shrink-0',
            hasTitle && 'mt-px',
            // single-line row is items-center; pin the icon to the top so a
            // wrapped (multi-line) description keeps the icon at the top-left.
            !hasTitle && 'self-start',
            TITLE_TEXT_BY_COLOR[color]
          )}
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {hasTitle ? (
          <>
            <p
              className={cn(
                'text-base font-semibold leading-6',
                TITLE_TEXT_BY_COLOR[color]
              )}
            >
              {title}
            </p>
            <div
              className={cn(
                'text-sm font-normal leading-5',
                BODY_TEXT_BY_COLOR[color]
              )}
            >
              {description}
            </div>
          </>
        ) : (
          <div
            className={cn(
              'text-sm font-normal leading-5',
              TITLE_TEXT_BY_COLOR[color]
            )}
          >
            {description}
          </div>
        )}
        {actionRow}
      </div>
      {dismissButton}
    </div>
  )
}
